/**
 * ============================================================================
 *  LIVE ELECTION-NIGHT FILE  —  EDIT THIS FILE TO UPDATE THE LIVE PAGE
 * ============================================================================
 *
 *  HOW TO USE ON ELECTION NIGHT (manual newsroom workflow):
 *
 *  1. Set  isLive: true   to switch the site into Live mode (red "LIVE" badge).
 *     Set it back to false after the night to return to the Analysis view.
 *
 *  2. Update  lastUpdated  every time you make changes (any text,
 *     e.g. "11 July 2026, 10:45 PM").
 *
 *  3. The confirmed 2026 candidate line-up for every seat is already loaded in
 *     `candidatesByContest` below (sourced from the EC nomination list, 27 June
 *     2026). Clicking any seat shows "Candidates · 2026" with the 2022 result
 *     below for comparison — even before any votes are counted.
 *
 *  4. As results arrive, add an entry per seat in  seatStatus :
 *       status:    "leading" | "won" | "pending"
 *                    - "leading" = ahead while counting (pulsing on the map)
 *                    - "won"     = declared / called
 *                    - "pending" = no result yet (you can omit the seat)
 *       party:     BN | PH | PN | MUDA | BERSAMA | PSM | ASLI | IND  (colour)
 *       candidate: name of the leading/winning candidate (optional)
 *       majority:  number (optional, fill in when known)
 *       sub:       coalition component, e.g. "DAP", "UMNO", "BERSATU" (optional)
 *       turnoutPct, registered, valid: 2026 numbers (optional, fill in later)
 *       votes:     to show the full 2026 breakdown with vote counts, add a
 *                  `votes` number to the matching candidate in
 *                  `candidatesByContest` (or leave names only before counting).
 *
 *     You only need to list seats that are leading/won. Anything not listed
 *     is treated as "pending". Only status + party are required; everything
 *     else can be filled in progressively through the night and after.
 *
 *  5. Add short lines to  feed  (newest first) — these appear in the
 *     "Latest updates" ticker. Keep them short and factual.
 *
 *  Tip: the running tally (who's leading / who's won) is calculated
 *  automatically from seatStatus — you do not need to count by hand.
 * ============================================================================
 */

export type LiveStatus = "leading" | "won" | "won_unofficial" | "won_official" | "pending";

export interface LiveCandidate {
  name: string;
  party: string; // BN | PH | PN | MUDA | BERSAMA | PSM | ASLI | IND
  sub?: string; // component party, e.g. "DAP", "UMNO", "PAS", "BERSATU"
  votes?: number; // add on the night to show the breakdown
}

export interface SeatLive {
  status: LiveStatus;
  party: string;
  candidate?: string;
  sub?: string;
  majority?: number;
  // optional 2026 numbers (fill in progressively)
  registered?: number;
  valid?: number;
  turnoutPct?: number;
}

export interface LiveUpdate {
  date?: string; // e.g. "11 Jul 2026"
  time: string; // e.g. "10:42 PM"
  text: string;
  linkTitle?: string | null; // optional: admin-provided headline for article links
}

export interface LiveConfig {
  isLive: boolean;
  electionName: string;
  level: "state" | "parliament"; // which map the live results apply to
  totalSeats: number;
  majorityNeeded: number; // seats needed to form government / majority
  lastUpdated: string;
  sourceNote: string;
  feed: LiveUpdate[];
  // keyed by seat code, e.g. "N25", "N12"
  seatStatus: Record<string, SeatLive>;
  // confirmed 2026 candidate line-up keyed by seat code (EC nomination list)
  candidatesByContest: Record<string, LiveCandidate[]>;
}

export const LIVE: LiveConfig = {
  // ⬇⬇⬇  Live mode is ON. Results are not in yet — all 56 seats are "pending"
  //       and show their confirmed 2026 candidates. Add results on the night.  ⬇⬇⬇
  isLive: true,
  electionName: "16th Johor State Election · Live",
  level: "state",
  totalSeats: 56,
  majorityNeeded: 29,
  lastUpdated: "27 June 2026 — Nomination Day. Candidates confirmed; polling 11 July.",
  sourceNote:
    "2026 candidate line-up: all 172 candidates confirmed by the Election Commission (SPR) on Nomination Day, 27 June 2026. Cross-checked with Free Malaysia Today (FMT) and Wikipedia. BN 56, PH 56, PN 33, Bersama 15, IND 6, MUDA 4, PSM 1, POAM 1. Component-party assignments sourced from SPR/Wikipedia. Results will be updated live on polling night, 11 July 2026.",

  // No results yet — polling day is 11 July 2026.
  // On the night, add seats here, e.g.  N12: { status: "won", party: "PH", sub: "DAP", candidate: "...", majority: 8200 }
  seatStatus: {},

  feed: [
    { date: "27 Jun 2026", time: "1:00 PM", text: "Nomination Day closes: 172 candidates confirmed across all 56 Johor state seats. Polling on 11 July." },
    { date: "27 Jun 2026", time: "12:30 PM", text: "BN and PH each contest all 56 seats; PN fields 33, Bersama 15, MUDA 4, PSM 1, plus independents. Biggest five-cornered fights: Bukit Naning, Puteri Wangsa, Bukit Batu." },
    { date: "27 Jun 2026", time: "10:00 AM", text: "Nomination centres open across Johor's 56 state constituencies." },
  ],

  // ── Confirmed 2026 candidates per seat (EC nomination list, 27 June 2026) ──
  // Names only for now; add `votes` to each on counting night to show the breakdown.
  // `sub` = component party (e.g. UMNO, DAP, PAS). Source: Wikipedia / SPR.
  candidatesByContest: {
    "N01": [
      { name: "Rozy Razit", party: "PH", sub: "PKR" },
      { name: "Zahari Sarip", party: "BN", sub: "UMNO" },
    ],
    "N02": [
      { name: "Ng Kor Sim", party: "PH", sub: "DAP" },
      { name: "See Ann Giap", party: "BN", sub: "MCA" },
      { name: "Saifullah Abdul Wahab", party: "PN", sub: "PAS" },
    ],
    "N03": [
      { name: "Jalex Lee", party: "PH", sub: "PKR" },
      { name: "Anuar Abdul Manap", party: "BN", sub: "UMNO" },
      { name: "Dr A Ariventharan", party: "PN", sub: "MIPP" },
    ],
    "N04": [
      { name: "Afif Abdul Hamid", party: "PH", sub: "AMANAH" },
      { name: "K Raven Kumar", party: "BN", sub: "MIC" },
      { name: "Uzzair Ismail", party: "PN", sub: "BERSATU" },
    ],
    "N05": [
      { name: "Elia Nadira Sabudin", party: "PH", sub: "AMANAH" },
      { name: "Azahar Ibrahim", party: "BN", sub: "UMNO" },
      { name: "Normala Sudirman", party: "PN", sub: "PAS" },
      { name: "Siti Aisyah Zobir", party: "IND", sub: "IND" },
    ],
    "N06": [
      { name: "Tay Yok Jiuen", party: "PH", sub: "DAP" },
      { name: "Tan Chong", party: "BN", sub: "MCA" },
    ],
    "N07": [
      { name: "C Subramani", party: "PH", sub: "PKR" },
      { name: "Ahmad Syar'e Yusof", party: "BN", sub: "UMNO" },
      { name: "Dr Sahruddin Jamal", party: "PN", sub: "BERSATU" },
    ],
    "N08": [
      { name: "Najib Lep", party: "PH", sub: "AMANAH" },
      { name: "Fazli Salleh", party: "BN", sub: "UMNO" },
      { name: "Idzhar Nasirruddin", party: "PN", sub: "BERSATU" },
    ],
    "N09": [
      { name: "Nor Yusof", party: "PH", sub: "PKR" },
      { name: "Sahrihan Jani", party: "BN", sub: "UMNO" },
      { name: "Suraya Sulaiman", party: "PN", sub: "PEJUANG" },
    ],
    "N10": [
      { name: "Ee Chin Li", party: "PH", sub: "DAP" },
      { name: "Haw Chin Teck", party: "BN", sub: "MCA" },
    ],
    "N11": [
      { name: "Ahmad Nazari Abdul Hamid", party: "PH", sub: "AMANAH" },
      { name: "Nadhirah Afiqah Abdull Rahim", party: "BN", sub: "UMNO" },
      { name: "Mahfidz Omar", party: "PN", sub: "BERSATU" },
    ],
    "N12": [
      { name: "Ng Yak Howe", party: "PH", sub: "DAP" },
      { name: "Chua Lee Huat", party: "BN", sub: "MCA" },
    ],
    "N13": [
      { name: "Ainie Haziqah Shafii", party: "MUDA", sub: "MUDA" },
      { name: "Nazri Abdul Rahman", party: "PH", sub: "AMANAH" },
      { name: "Azman Ismail", party: "BN", sub: "UMNO" },
      { name: "Arshed Yahya @Awang", party: "PN", sub: "PAS" },
    ],
    "N14": [
      { name: "Ysahrudin Kusni", party: "PH", sub: "PKR" },
      { name: "Ghazali Sabari", party: "BN", sub: "UMNO" },
      { name: "Radzi Amin", party: "PN", sub: "BERSATU" },
      { name: "Iskandar Alias", party: "BERSAMA", sub: "BERSAMA" },
      { name: "Jeghanaathan", party: "IND", sub: "IND" },
    ],
    "N15": [
      { name: "Amir Jack", party: "MUDA", sub: "MUDA" },
      { name: "Taqiuddin Cheman", party: "PH", sub: "AMANAH" },
      { name: "Ashari Sarip", party: "BN", sub: "UMNO" },
      { name: "Anuar Hayan", party: "PN", sub: "PAS" },
    ],
    "N16": [
      { name: "Soraya Badaruddin", party: "PH", sub: "PKR" },
      { name: "Selamat Takim", party: "BN", sub: "UMNO" },
      { name: "Amin Sailan", party: "PN", sub: "PAS" },
    ],
    "N17": [
      { name: "Khuzzan Abu Bakar", party: "PH", sub: "PKR" },
      { name: "Fared Khalid", party: "BN", sub: "UMNO" },
      { name: "Halim Kepol", party: "PN", sub: "PAS" },
    ],
    "N18": [
      { name: "Hishamuddin Ishak", party: "PH", sub: "PKR" },
      { name: "Zulkurnain Kamisan", party: "BN", sub: "UMNO" },
      { name: "Rosdi Bahari", party: "PN", sub: "PAS" },
    ],
    "N19": [
      { name: "Yong Hui Yi", party: "PH", sub: "DAP" },
      { name: "Ling Tian Soon", party: "BN", sub: "MCA" },
    ],
    "N20": [
      { name: "Ramli Abd Hamid", party: "PH", sub: "AMANAH" },
      { name: "Samsolbari Jamali", party: "BN", sub: "UMNO" },
      { name: "Syafiq Aziz", party: "PN", sub: "BERSATU" },
    ],
    "N21": [
      { name: "Ezam Taslim", party: "PH", sub: "AMANAH" },
      { name: "Najib Samuri", party: "BN", sub: "UMNO" },
    ],
    "N22": [
      { name: "Shazwan Zdainal", party: "PH", sub: "DAP" },
      { name: "Rashidah Ramli", party: "BN", sub: "UMNO" },
      { name: "Maliki Rapiee", party: "PN", sub: "BERSATU" },
    ],
    "N23": [
      { name: "Poh Rui Ling", party: "PH", sub: "DAP" },
      { name: "Boo Chin Liong", party: "BN", sub: "MCA" },
    ],
    "N24": [
      { name: "Onn Abu Bakar", party: "PH", sub: "PKR" },
      { name: "Yusla Ismail", party: "BN", sub: "UMNO" },
      { name: "Rashid Hasnon", party: "PN", sub: "BERSATU" },
    ],
    "N25": [
      { name: "Yazid Bakri", party: "PH", sub: "AMANAH" },
      { name: "Zaidi Japar", party: "BN", sub: "UMNO" },
      { name: "Syed Mohamad Syed Alwi", party: "PN", sub: "BERSATU" },
    ],
    "N26": [
      { name: "Hafiz Roslan", party: "PH", sub: "AMANAH" },
      { name: "Onn Hafiz Ghazi", party: "BN", sub: "UMNO" },
    ],
    "N27": [
      { name: "B Guna", party: "PH", sub: "PKR" },
      { name: "Chua Jian Boon", party: "BN", sub: "MCA" },
      { name: "Abd Mutalip Abd Rahim", party: "PN", sub: "BERSATU" },
    ],
    "N28": [
      { name: "Chu Poh Yee", party: "PH", sub: "DAP" },
      { name: "Yap Zhi Peng", party: "BN", sub: "MCA" },
    ],
    "N29": [
      { name: "Ahmad Zuhan Zain", party: "PH", sub: "AMANAH" },
      { name: "Syed Hussien Syed Abdullah", party: "BN", sub: "UMNO" },
      { name: "Abdul Hamid Ali", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N30": [
      { name: "Dr A Ruban", party: "PH", sub: "DAP" },
      { name: "Lee Ting Han", party: "BN", sub: "MCA" },
      { name: "D Jeevakumar", party: "PN", sub: "MIPP" },
      { name: "G Kamaleswaren", party: "IND", sub: "IND" },
    ],
    "N31": [
      { name: "Sabri Kadir", party: "PH", sub: "AMANAH" },
      { name: "V Rugendran", party: "BN", sub: "MIC" },
      { name: "Mazlan Bujang", party: "PN", sub: "PAS" },
    ],
    "N32": [
      { name: "Saiful Nizam Samat", party: "PH", sub: "PKR" },
      { name: "Alwiyah Talib", party: "BN", sub: "UMNO" },
      { name: "Hasnul Hakimi Hussien", party: "PN", sub: "PAS" },
      { name: "Jati Awang", party: "POAM", sub: "POAM" },
    ],
    "N33": [
      { name: "Yusof Dawam", party: "PH", sub: "PKR" },
      { name: "Youzaimi Yusof", party: "BN", sub: "UMNO" },
      { name: "Amerul Muhamad", party: "PN", sub: "BERSATU" },
    ],
    "N34": [
      { name: "Daniel Sharudin", party: "PH", sub: "AMANAH" },
      { name: "Naqib Ghazali", party: "BN", sub: "UMNO" },
      { name: "Alias Rasman", party: "PN", sub: "BERSATU" },
    ],
    "N35": [
      { name: "Fakharuddin Moslim", party: "PH", sub: "PKR" },
      { name: "Dr Adham Baba", party: "BN", sub: "UMNO" },
      { name: "Yuhanita Yunan", party: "PN", sub: "PAS" },
    ],
    "N36": [
      { name: "Amirul Huzni Onn", party: "PH", sub: "AMANAH" },
      { name: "Muszaide Makmor", party: "BN", sub: "UMNO" },
      { name: "Rasman Ithnain", party: "PN", sub: "BERSATU" },
    ],
    "N37": [
      { name: "Danish Rahman", party: "PH", sub: "PKR" },
      { name: "Norlizah Noh", party: "BN", sub: "UMNO" },
      { name: "Aisah Esa", party: "PN", sub: "BERSATU" },
    ],
    "N38": [
      { name: "Sawaludin Salleh", party: "PH", sub: "AMANAH" },
      { name: "Fauziah Misri", party: "BN", sub: "UMNO" },
      { name: "Fairulnizar Rahmat", party: "PN", sub: "BERSATU" },
    ],
    "N39": [
      { name: "Faizul Abdul Ghani", party: "PH", sub: "PKR" },
      { name: "Aznan Tamin", party: "BN", sub: "UMNO" },
    ],
    "N40": [
      { name: "Nor Zulaila Abd Ghani", party: "PH", sub: "DAP" },
      { name: "Abdul Halim Suleiman", party: "BN", sub: "UMNO" },
      { name: "Khirul Muntanazar Ismail", party: "PN", sub: "PAS" },
      { name: "Harith Fakhrudin Abdul Malik", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N41": [
      { name: "Rashifa Aljuneid", party: "MUDA", sub: "MUDA" },
      { name: "Maszlee Malik", party: "PH", sub: "PKR" },
      { name: "Teow Chia Ling", party: "BN", sub: "MCA" },
      { name: "Nicholas Paul Vincent", party: "BERSAMA", sub: "BERSAMA" },
      { name: "Wang Wee Siong", party: "IND", sub: "IND" },
    ],
    "N42": [
      { name: "Lee Wern Ying", party: "PH", sub: "DAP" },
      { name: "Chan San San", party: "BN", sub: "MCA" },
      { name: "Lau Yi Leong", party: "BERSAMA", sub: "BERSAMA" },
      { name: "HP Lim", party: "IND", sub: "IND" },
    ],
    "N43": [
      { name: "Teo Siew Hui", party: "PH", sub: "AMANAH" },
      { name: "Baharudin Taib", party: "BN", sub: "UMNO" },
      { name: "T Vela", party: "PN", sub: "MIPP" },
      { name: "Dr Zamil Najwah", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N44": [
      { name: "Suhaizan Kaiat", party: "PH", sub: "AMANAH" },
      { name: "Hairi Mad Shah", party: "BN", sub: "UMNO" },
      { name: "Norsinah Abu", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N45": [
      { name: "Andrew Chen", party: "PH", sub: "DAP" },
      { name: "Bong Seng Heng", party: "BN", sub: "MCA" },
      { name: "Lim Chin Eng", party: "PN", sub: "BERSATU" },
      { name: "Stanley Tan", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N46": [
      { name: "Tee Boon Tsong", party: "PH", sub: "DAP" },
      { name: "B Pannir Selvam", party: "BN", sub: "MIC" },
      { name: "Boo Wei Han", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N47": [
      { name: "Faezuddin Puad", party: "PH", sub: "PKR" },
      { name: "Ramlee Bohani", party: "BN", sub: "UMNO" },
      { name: "Salamahafifi Yusnaieny", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N48": [
      { name: "J Kartiyaini", party: "PH", sub: "DAP" },
      { name: "Tan Hiang Kee", party: "BN", sub: "MCA" },
      { name: "Amir Syafiq Ameer Soekre", party: "PSM", sub: "PSM" },
      { name: "Eugene Chua", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N49": [
      { name: "Dzulkefly Ahmad", party: "PH", sub: "AMANAH" },
      { name: "Pandak Ahmad", party: "BN", sub: "UMNO" },
      { name: "S Anna Pravina", party: "PN", sub: "MIPP" },
      { name: "Sahrudin Omar", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N50": [
      { name: "Shafwan Ani", party: "PH", sub: "DAP" },
      { name: "Jafni Shukor", party: "BN", sub: "UMNO" },
      { name: "M Lina Manoh", party: "PN", sub: "MIPP" },
      { name: "Aidil Riduan Yusof", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N51": [
      { name: "Arthur Chiong", party: "PH", sub: "PKR" },
      { name: "M Premanand", party: "MUDA", sub: "MUDA" },
      { name: "R Kumaran", party: "BN", sub: "MIC" },
      { name: "G Tamili", party: "BERSAMA", sub: "BERSAMA" },
      { name: "Kamaruzaman Ali", party: "IND", sub: "IND" },
    ],
    "N52": [
      { name: "Wong Bor Yang", party: "PH", sub: "DAP" },
      { name: "Tai Chee Chee", party: "BN", sub: "MCA" },
      { name: "Tew Chian How", party: "BERSAMA", sub: "BERSAMA" },
    ],
    "N53": [
      { name: "Abdul Razak Ismail", party: "PH", sub: "AMANAH" },
      { name: "Sumali Reduan", party: "BN", sub: "UMNO" },
    ],
    "N54": [
      { name: "Haniff Hosman", party: "PH", sub: "PKR" },
      { name: "Hasrunizah Hassan", party: "BN", sub: "UMNO" },
    ],
    "N55": [
      { name: "Yeo Tung Siong", party: "PH", sub: "DAP" },
      { name: "Tan Eng Meng", party: "BN", sub: "MCA" },
    ],
    "N56": [
      { name: "Cheah Chee Hong", party: "PH", sub: "PKR" },
      { name: "Israk Abdullah", party: "BN", sub: "UMNO" },
    ],
  },
};
